import React, { useEffect, useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { IconTextButton } from "../../components/ButtonComponents";
import { Save, Globe, FileText, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { useEInvoiceStore } from "../../store/eInvoiceStore";

const SettingRow = ({ title, description, checked, onChange }: any) => (
  <div className="group flex items-center justify-between p-4 rounded-xl bg-card border border-borderColor hover:border-primary hover:shadow-md transition-all duration-300 hover:bg-bgAlt cursor-pointer" onClick={() => onChange({ value: !checked })}>
    <div className="flex flex-col gap-0.5">
      <span className="text-[13px] font-bold text-text opacity-90 group-hover:text-primary transition-colors">{title}</span>
      <span className="text-[11px] text-text opacity-60 leading-tight">{description}</span>
    </div>
    <div className="pl-2 shrink-0" onClick={(e) => e.stopPropagation()}>
      <InputSwitch checked={checked} onChange={onChange} />
    </div>
  </div>
);

const SettingSection = ({ title, icon: Icon, colorClass, children }: any) => (
  <div className="flex flex-col shadow-sm card p-0 overflow-hidden">
    <div className="card2 shadow-none border-none p-2 flex items-center gap-3">
      {Icon && (
        <div className={`p-2 rounded-lg ${colorClass}`}>
          <Icon size={18} />
        </div>
      )}
      <h3 className="font-bold text-text">{title}</h3>
    </div>
    <div className="p-2 space-y-3">
      {children}
    </div>
  </div>
);

export const EInvoiceConfig: React.FC = () => {
  const { config, fetchConfig, saveConfig } = useEInvoiceStore();
  const [localInvoice, setLocalInvoice] = useState<any>(null);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if (config) {
      setLocalInvoice({ ...config });
    } else {
      setLocalInvoice({ provider: 'NES_TEKNOLOJI' });
    }
  }, [config]);

  const handleSave = async () => {
    if (!localInvoice) return;
    const success = await saveConfig(localInvoice);
    if (success) {
      toast.success("E-Fatura ayarları başarıyla kaydedildi.");
    } else {
      toast.error("E-Fatura ayarları kaydedilemedi.");
    }
  };

  return (
    <div className="p-4 space-y-6">
      <div className="hidden md:flex items-center justify-between sticky top-0 z-50 bg-bgAlt backdrop-blur-md -mx-4 -mt-4 p-3 border-b border-borderColor shadow-sm mb-6 rounded-t-lg">
        <div className="flex flex-col">
          <h3 className="font-bold text-sm text-text md:block">E-Fatura Bağlantısı</h3>
          <p className="text-[11px] text-text opacity-60 hidden md:block">Entegratör bağlantı bilgilerinizi ve serileri yapılandırın.</p>
        </div>
        <IconTextButton icon={Save} className="btn-new shadow-md whitespace-nowrap" onClick={handleSave}>Kaydet</IconTextButton>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SettingSection title="Entegratör Bağlantısı" icon={Globe} colorClass="bg-primary/10 text-primary">
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold">Sağlayıcı</label>
            <Dropdown 
              value={localInvoice?.provider || 'NES_TEKNOLOJI'} 
              options={[{ label: 'NES Teknoloji', value: 'NES_TEKNOLOJI' }]} 
              onChange={(e) => setLocalInvoice({ ...localInvoice, provider: e.value })} 
              className="p-dropdown-sm w-full" 
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold">Kullanıcı Adı</label>
            <InputText 
              value={localInvoice?.username || ""} 
              onChange={(e) => setLocalInvoice({ ...localInvoice, username: e.target.value })} 
              className="p-inputtext-sm w-full" 
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold">Şifre</label>
            <InputText 
              type="password" 
              value={localInvoice?.password || ""} 
              onChange={(e) => setLocalInvoice({ ...localInvoice, password: e.target.value })} 
              className="p-inputtext-sm w-full" 
            />
          </div>
          <div className="flex gap-4 pt-2">
            <SettingRow
              title="Sistem Aktif"
              description="E-Fatura entegrasyonu kullanıma açılır."
              checked={localInvoice?.isActive || false}
              onChange={(e: any) => setLocalInvoice({ ...localInvoice, isActive: e.value })}
            />
          </div>
          <div className="flex gap-4">
            <SettingRow
              title="Test Modu"
              description="Test ortamında işlem yapılır."
              checked={localInvoice?.isTestMode || false}
              onChange={(e: any) => setLocalInvoice({ ...localInvoice, isTestMode: e.value })}
            />
          </div>
        </SettingSection>

        <SettingSection title="Seri ve Dizayn" icon={FileText} colorClass="bg-primary/10 text-primary">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold">Fatura Serisi</label>
              <InputText 
                value={localInvoice?.invoiceSeries || ""} 
                onChange={(e) => setLocalInvoice({ ...localInvoice, invoiceSeries: e.target.value })} 
                maxLength={3} 
                className="p-inputtext-sm uppercase" 
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold">Arşiv Serisi</label>
              <InputText 
                value={localInvoice?.archiveSeries || ""} 
                onChange={(e) => setLocalInvoice({ ...localInvoice, archiveSeries: e.target.value })} 
                maxLength={3} 
                className="p-inputtext-sm uppercase" 
              />
            </div>
          </div>
          <IconTextButton icon={RefreshCw} className="w-full text-xs mt-2" onClick={() => toast.info("Servis bağlantısı kontrol ediliyor...")}>
            NES'ten Tasarımları Sorgula
          </IconTextButton>
        </SettingSection>
      </div>
    </div>
  );
};
